/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import { ARViewerWeb } from '.'

/** This plugin checks for device motion support and throws an error if not found. */
export default class DeviceMotionCheckPlugin {
  ar: ARViewerWeb

  constructor(ar: ARViewerWeb) {
    // Store AR
    this.ar = ar
  }

  get name() {
    return 'DeviceMotionCheckPlugin'
  }

  async load(div: HTMLDivElement) {
    // Show status
    this.ar.setState({ loadingText: 'Checking motion support...' })

    // Check if the motion event class exists
    if (typeof DeviceOrientationEvent === 'undefined')
      throw new Error('Sorry, it looks like your browser does not support device motion events.')

    // If on iOS 13, call the permissions API to see the status
    // @ts-ignore
    if (DeviceOrientationEvent.requestPermission) {
      // HACK: Request motion access. This is going to get messy, since there is no way to detect current status (that I know of), and you
      // can only request permission from a user gesture. So we will need to try request permission, if it fails then show an overlay button. On that
      // button's press, request permission again and use the value of that.
      let result = ''
      try {
        // First, check if permission has already been granted
        // @ts-ignore
        result = await DeviceOrientationEvent.requestPermission()
      } catch (err) {
        // Failed! Most likely because this isn't a touch event.
        console.warn(
          `[AR] Failed to request motion access! We'll ask the user to grant permission now.`
        )
        result = await this.showPermissionPrompt(div)
      }

      // Check the end result
      if (result === 'denied')
        throw new Error(
          'Permission denied while accessing the motion sensors. Check your browser settings, or try quit and reopen the browser.'
        )
      else if (result !== 'granted')
        throw new Error('Access to motion sensors have not been granted by your browser.')

      // Success!
      console.info('Motion check good')
      return
    }

    // Create a flag which will be set to true if a motion event is received
    let didReceiveEvent = false
    const eventHandler = function (e: any) {
      didReceiveEvent = true
    }
    window.addEventListener('deviceorientation', eventHandler)

    // Wait a while for the event to be received
    const startTime = Date.now()
    const timeout = 5000
    while (Date.now() - startTime < timeout && !didReceiveEvent)
      await new Promise(resolve => setTimeout(resolve, 250))

    // Remove listener
    window.removeEventListener('deviceorientation', eventHandler)

    // If event received, stop
    if (didReceiveEvent) {
      console.info('Motion event received')
      return
    }

    // Device never received a motion event! Check if it's an iPhone
    const iOS = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)
    if (iOS)
      throw new Error(
        "This experience only works on mobile devices that have motion sensors. If you're on an iPhone, this may have been disabled by Apple.\n\nYou can turn it on by going to Settings > Safari > Motion & Orientation Access."
      )

    // Another device, not iOS
    throw new Error(
      'This experience only works on mobile devices that have motion sensors. Your device may not have the required hardware, or this feature may be disabled by your browser or operating system.'
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  unload() {}

  /** Shows an overlay permission prompt UI. */
  showPermissionPrompt(parentDiv: HTMLDivElement): Promise<PermissionState> {
    // const primaryColor =
    // 	this.ar.customSession?.config?.header?.color || "#0088ff";
    const primaryColor = '#0088ff'
    // Create container
    const div = document.createElement('div')
    div.style.cssText =
      'position: absolute; top: 0px; left: 0px; width: 100%; height: 100%; z-index: 8; background-color: #25272b; display: flex; flex-direction: column; align-items: center; justify-content: center; '
    parentDiv.appendChild(div)

    // Add label
    let lbl = document.createElement('div')
    lbl.style.cssText =
      'color: #FFF; font-size: 1rem; text-align: center; margin: 0px 40px 8px 40px; '
    lbl.innerText = 'Motion access'
    div.appendChild(lbl)

    // Add label
    lbl = document.createElement('div')
    lbl.style.cssText =
      'color: #DDD; font-size: 0.882rem; text-align: center; margin: 0px 40px 32px 40px; '
    lbl.innerText = 'We need access to the motion sensors in order to use this feature.'
    div.appendChild(lbl)

    // Add button
    const btn = document.createElement('div')
    btn.style.cssText = `color: white; font-size: 0.882rem; text-align: center; margin-bottom: 32px; padding: 16px 40px; background-color: ${primaryColor}; cursor: pointer; border-radius: 8px; `
    btn.innerText = 'Grant access'
    div.appendChild(btn)

    // Create promise
    return new Promise((resolve, reject) => {
      // Add click handler for button
      btn.addEventListener('click', e => {
        // Request access again, and resolve
        Promise.resolve()
          // @ts-ignore
          .then(e => DeviceOrientationEvent.requestPermission())
          .then(resolve)
          .catch(err => reject(err))

        // Remove UI
        parentDiv.removeChild(div)
      })
    })
  }
}
